import React, { useState } from 'react';
import { Icon } from '@iconify/react';
import { useTheme } from '../../contexts/ThemeContext';
import apiClient from '../../services/api/apiClient';

function ReplyBox({ postId, accountId, postContent, replyUsername, onReplySent }) {
  const { theme } = useTheme();
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingAI, setLoadingAI] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Get AI suggestion
  const handleGetAISuggestion = async () => {
    if (!postContent) return;

    try {
      setLoadingAI(true);
      setError(null);

      const response = await apiClient.post('/threads/insights/suggest-reply', {
        postContent,
        replyText: postContent,
        replyUsername,
        tone: 'friendly'
      });

      if (response.data.success && response.data.data?.suggestion) {
        setReplyText(response.data.data.suggestion);
      }
    } catch (err) {
      console.error('Failed to get AI suggestion:', err);
      setError('Failed to generate AI suggestion');
    } finally {
      setLoadingAI(false);
    }
  };

  // Send reply
  const handleSendReply = async () => {
    if (!replyText.trim() || !accountId) return;

    try {
      setSending(true);
      setError(null);
      setSuccess(false);

      const response = await apiClient.post('/threads/insights/send-reply', {
        postId,
        text: replyText.trim(),
        accountId
      });

      if (response.data.success) {
        setSuccess(true);
        setReplyText('');
        setTimeout(() => setSuccess(false), 3000);
        if (onReplySent) onReplySent();
      }
    } catch (err) {
      console.error('Failed to send reply:', err);
      setError(err.response?.data?.message || 'Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-3">
      {/* Textarea */}
      <textarea
        value={replyText}
        onChange={(e) => setReplyText(e.target.value)}
        placeholder="Write your reply..."
        rows={4}
        className="w-full px-3 py-2 rounded-lg border resize-none outline-none text-sm"
        style={{
          background: theme.bgPrimary,
          borderColor: theme.borderColor,
          color: theme.textPrimary
        }}
      />

      {/* Character count */}
      <div className="flex items-center justify-between">
        <span className="text-xs" style={{ color: theme.textTertiary }}>
          {replyText.length} / 500 characters
        </span>
        {success && (
          <span className="text-xs font-medium flex items-center gap-1" style={{ color: '#10b981' }}>
            <Icon icon="mdi:check-circle" />
            Sent successfully!
          </span>
        )}
      </div>

      {/* Error message */}
      {error && (
        <div className="px-3 py-2 rounded-lg text-xs" style={{ background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444' }}>
          {error}
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex gap-2">
        <button
          onClick={handleGetAISuggestion}
          disabled={loadingAI || sending}
          className="flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-opacity hover:opacity-80 disabled:opacity-50 flex items-center justify-center gap-2"
          style={{ background: theme.bgHover, color: theme.textPrimary }}
        >
          {loadingAI ? (
            <>
              <Icon icon="mdi:loading" className="animate-spin" />
              Generating...
            </>
          ) : (
            <>
              <Icon icon="mdi:lightbulb-outline" />
              AI Suggest
            </>
          )}
        </button>
        <button
          onClick={handleSendReply}
          disabled={!replyText.trim() || sending || !accountId}
          className="flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-opacity hover:opacity-80 disabled:opacity-50 flex items-center justify-center gap-2"
          style={{ background: theme.buttonPrimary, color: theme.buttonPrimaryText }}
        >
          {sending ? (
            <>
              <Icon icon="mdi:loading" className="animate-spin" />
              Sending...
            </>
          ) : (
            <>
              <Icon icon="mdi:send" />
              Send Reply
            </>
          )}
        </button>
      </div>

      {!accountId && (
        <p className="text-xs text-center" style={{ color: '#ef4444' }}>
          Please select a Threads account first
        </p>
      )}
    </div>
  );
}

export default ReplyBox;
